/**
 * Stato della vista inbox: account selezionato, cartella, filtri.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { FolderRole } from '@/lib/api/endpoints';

interface InboxState {
  selectedAccountId: string | null;
  selectedFolderRole: FolderRole;
  selectedCategory: string | null;
  selectedLabel: string | null;
  selectedUrgency: string | null;
  unreadOnly: boolean;
  starredOnly: boolean;
  selectedMessageId: string | null;

  setAccount: (id: string | null) => void;
  setFolder: (role: FolderRole) => void;
  setCategory: (cat: string | null) => void;
  setLabel: (label: string | null) => void;
  setUrgency: (u: string | null) => void;
  toggleUnreadOnly: () => void;
  toggleStarredOnly: () => void;
  selectMessage: (id: string | null) => void;
  resetFilters: () => void;
}

export const useInbox = create<InboxState>()(
  persist(
    (set) => ({
      selectedAccountId: null,
      selectedFolderRole: 'inbox',
      selectedCategory: null,
      selectedLabel: null,
      selectedUrgency: null,
      unreadOnly: false,
      starredOnly: false,
      selectedMessageId: null,

      setAccount: (id) => set({ selectedAccountId: id, selectedMessageId: null }),
      setFolder: (role) =>
        set({
          selectedFolderRole: role,
          selectedCategory: null,
          selectedLabel: null,
          selectedUrgency: null,
          selectedMessageId: null,
          starredOnly: false,
          unreadOnly: false,
        }),
      // Categorie ed etichette sono viste cross-cartella: riportano sempre il
      // contesto a 'inbox', altrimenti il filtro resterebbe ristretto all'ultima
      // cartella visitata (es. Archivio) escludendo i thread in Inbox.
      setCategory: (cat) =>
        set({ selectedCategory: cat, selectedLabel: null, selectedFolderRole: 'inbox', selectedMessageId: null, selectedUrgency: null }),
      setLabel: (label) =>
        set({ selectedLabel: label, selectedCategory: null, selectedFolderRole: 'inbox', selectedMessageId: null, selectedUrgency: null }),
      setUrgency: (u) => set({ selectedUrgency: u, selectedMessageId: null }),
      toggleUnreadOnly: () =>
        set((s) => ({ unreadOnly: !s.unreadOnly, selectedMessageId: null })),
      toggleStarredOnly: () =>
        set((s) => ({ starredOnly: !s.starredOnly, selectedMessageId: null })),
      selectMessage: (id) => set({ selectedMessageId: id }),
      resetFilters: () =>
        set({
          selectedCategory: null,
          selectedLabel: null,
          selectedFolderRole: 'inbox',
          selectedUrgency: null,
          unreadOnly: false,
          starredOnly: false,
        }),
    }),
    {
      name: 'blagomail.inbox',
      partialize: (s) => ({
        selectedAccountId: s.selectedAccountId,
        selectedFolderRole: s.selectedFolderRole,
      }),
    }
  )
);
